/**
 * AnalyticsBody — the module container beneath the Analytics shell (CD-7). Owns the
 * surface's ONE windowed /resolve (site=all, sliced client-side, same call the
 * Overview uses) + the catalogue, and routes module.id to its module: variance
 * bridges (1) and pace (2) are built; drivers/benchmarking/contribution show the
 * honest "detail to follow" frame until their beat lands. Loading/error/empty states
 * are honest — a typed API error is a visible message, never a blank frame.
 */

import React from 'react'
import { AlertCircle } from 'lucide-react'
import InfoBanner from '../components/InfoBanner'
import { useMetrics } from '../hooks/useMetrics'
import { useResolve } from '../hooks/useResolve'
import { type Scope } from '../hooks/useScope'
import { type AnalyticsModule } from '../hooks/useAnalyticsView'
import BridgeModule from './BridgeModule'
import PaceModule from './PaceModule'
import AnalyticsModuleFrame from './AnalyticsModuleFrame'
import type { CatalogueResponse, ResolveResponse } from '../types/metrics'

const DATA_MODULES = new Set(['bridges', 'pace'])

function LoadingState(): React.JSX.Element {
  return (
    <div role="status" className="animate-pulse rounded-xl border border-line bg-panel px-4 py-12 text-center text-sm text-mut2">
      Loading analytics…
    </div>
  )
}

function BuiltModule({
  module,
  scope,
  catalogue,
  resolve,
}: {
  module: AnalyticsModule
  scope: Scope
  catalogue: CatalogueResponse
  resolve: ResolveResponse
}): React.JSX.Element {
  if (module.id === 'bridges') return <BridgeModule scope={scope} resolve={resolve} />
  return <PaceModule scope={scope} resolve={resolve} catalogue={catalogue} />
}

export default function AnalyticsBody({ scope, module }: { scope: Scope; module: AnalyticsModule }): React.JSX.Element {
  const catalogue = useMetrics()
  const resolve = useResolve(scope.periodFrom, scope.periodTo, scope.cadence, 'all')

  const renderContent = (): React.JSX.Element => {
    if (!DATA_MODULES.has(module.id)) return <AnalyticsModuleFrame module={module} scope={scope} />
    const error = resolve.error ?? catalogue.error
    if (error) {
      return <InfoBanner variant="error" icon={AlertCircle} title="Analytics unavailable" message={error.message} />
    }
    if (!catalogue.data || !resolve.data) return <LoadingState />
    if (resolve.data.cells.length === 0) {
      return (
        <InfoBanner
          variant="info"
          message={`No cells served for ${scope.periodFrom} → ${scope.periodTo} (${scope.cadence}). The window may predate the served history.`}
        />
      )
    }
    return <BuiltModule module={module} scope={scope} catalogue={catalogue.data} resolve={resolve.data} />
  }

  return (
    <div key={module.id} data-testid={`analytics-module-${module.id}`} className="cx-fade">
      {renderContent()}
    </div>
  )
}
